/**
 * authService.js - Centralized Authentication Service
 * ====================================================
 * Single source of truth for all auth operations.
 * NO retry loops, NO fallbacks, NO legacy code.
 * 100% Supabase.
 */

import supabase from '../lib/supabase';

/**
 * Get current authenticated user
 * Uses supabase.auth.getUser() - the ONLY correct method
 */
export async function getCurrentUser() {
  if (!supabase) return null;
  
  const { data: { user }, error } = await supabase.auth.getUser();
  
  if (error) {
    console.error('[AUTH] getUser error:', error.message);
    return null;
  }
  
  return user;
}

/**
 * Get current session
 */
export async function getCurrentSession() {
  if (!supabase) return null;
  
  const { data: { session }, error } = await supabase.auth.getSession();
  
  if (error) {
    console.error('[AUTH] getSession error:', error.message);
    return null;
  }
  
  return session;
}

/**
 * Get current user's profile
 * - Uses .single() as required
 * - Auto-creates profile if not found
 * - NO retry loops
 */
export async function getCurrentProfile() {
  if (!supabase) return null;
  
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user) {
    console.log('[AUTH] getCurrentProfile: No authenticated user');
    return null;
  }
  
  // Fetch profile with .single()
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .single();
  
  if (error) {
    // PGRST116 = no rows found - profile doesn't exist yet
    if (error.code === 'PGRST116') {
      console.log('[AUTH] Profile not found, creating...');
      return await createProfile(user);
    }
    
    // Other error - throw it
    console.error('[AUTH] Profile fetch error:', error.message);
    throw error;
  }
  
  return data;
}

/**
 * Create profile for new user
 * Called automatically if profile doesn't exist
 */
export async function createProfile(user) {
  if (!supabase || !user) return null;
  
  const newProfile = {
    id: user.id,
    email: user.email,
    onboarding_completed: false,
    onboarding_step: 0,
    created_at: new Date().toISOString()
  };
  
  const { data, error } = await supabase
    .from('profiles')
    .insert(newProfile)
    .select()
    .single();
  
  if (error) {
    console.error('[AUTH] Create profile error:', error.message);
    // If insert fails (maybe profile was created by trigger), try to fetch again
    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    
    return existingProfile || null;
  }
  
  console.log('[AUTH] Profile created successfully');
  return data;
}

/**
 * Update profile
 * Simple, direct update - no retries
 */
export async function updateProfile(userId, updates) {
  if (!supabase || !userId) {
    throw new Error('Missing supabase or userId');
  }
  
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();
  
  if (error) {
    console.error('[AUTH] Update profile error:', error.message);
    throw error;
  }
  
  return data;
}

/**
 * Complete onboarding
 * Sets onboarding_completed = true and redirects
 */
export async function completeOnboarding(userId, additionalData = {}) {
  if (!supabase || !userId) {
    throw new Error('Missing supabase or userId');
  }
  
  const updates = {
    onboarding_completed: true,
    onboarding_step: 3,
    ...additionalData
  };
  
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();
  
  if (error) {
    console.error('[AUTH] Complete onboarding error:', error.message);
    throw error;
  }
  
  return data;
}

/**
 * Check if profile is complete
 * Simple, deterministic check - accepts either business_type or role_professional
 */
export function isProfileComplete(profile) {
  if (!profile) return false;
  
  // Onboarding must be completed
  if (!profile.onboarding_completed) return false;
  
  // Must have either business_type (new) or role_professional (legacy)
  if (!profile.business_type && !profile.role_professional) return false;
  
  return true;
}

/**
 * Login with email/password
 */
export async function loginWithEmail(email, password) {
  if (!supabase) {
    throw new Error('Supabase not configured');
  }
  
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });
  
  if (error) {
    throw error;
  }
  
  return data;
}

/**
 * Logout
 */
export async function logout() {
  if (!supabase) return;
  
  const { error } = await supabase.auth.signOut();
  
  if (error) {
    console.error('[AUTH] Logout error:', error.message);
  }
}

export default {
  getCurrentUser,
  getCurrentSession,
  getCurrentProfile,
  createProfile,
  updateProfile,
  completeOnboarding,
  isProfileComplete,
  loginWithEmail,
  logout
};
